import {ChangeDetectionStrategy, Component, computed, inject, OnInit, ViewChild} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatInputModule} from "@angular/material/input"
import {MatSelectModule} from "@angular/material/select"
import {MatTooltipModule} from "@angular/material/tooltip"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {ActionPanelComponent} from "@common/components/menu/actions/action-panel/action-panel.component"
import {AutoTilingState, AutoTilingViewMode} from "@app/textures/texture-editor/operator-stack/operators/auto-tiling/operator-auto-tiling-types"
import type {OperatorAutoTiling} from "@app/textures/texture-editor/operator-stack/operators/auto-tiling/operator-auto-tiling"
import * as Tiling from "@app/textures/texture-editor/operator-stack/operators/auto-tiling/service/auto-tiling.service"
import {HintComponent} from "@app/common/components/hint/hint.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {ActionPanelRowComponent} from "@app/common/components/menu/actions/action-panel-row/action-panel-row.component"
import {OperatorPanelBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-panel-base"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-auto-tiling-panel",
    templateUrl: "./auto-tiling-panel.component.html",
    styleUrls: ["./auto-tiling-panel.component.scss"],
    imports: [
        HintComponent,
        ButtonComponent,
        InspectorSectionComponent,
        MatTooltipModule,
        ActionPanelRowComponent,
        MatInputModule,
        MatSelectModule,
        FormsModule,
    ],
})
export class AutoTilingPanelComponent extends OperatorPanelBase<OperatorAutoTiling> implements OnInit {
    @ViewChild("panel", {static: true}) panel!: ActionPanelComponent

    readonly permission = inject(PermissionsService)
    $can = this.permission.$to

    protected ViewMode = AutoTilingViewMode

    ngOnInit(): void {}

    show(): void {
        this.panel.show()
    }

    hide(): void {
        this.panel.hide()
    }

    readonly $isEdited = computed(() => this.$operator().$edited())

    readonly $canChangeAutoTilingSetup = computed(() => this.$operator().$canChangeAutoTilingSetup())

    readonly $viewMode = computed(() => this.$operator().$viewMode())

    protected setViewMode(value: AutoTilingViewMode) {
        this.$operator().setViewMode(value)
    }

    readonly $isInProgress = computed(() => this.$operator().$autoTilingState() === AutoTilingState.InProgress)

    readonly $isComplete = computed(() => this.$operator().$autoTilingState() === AutoTilingState.Complete)

    readonly $processingMode = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.processingMode
    })

    protected setProcessingMode(value: Tiling.ProcessingMode) {
        this.$operator().setProcessingMode(value)
    }

    readonly $featureWeighting = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.featureWeighting
    })

    protected setFeatureWeighting(value: Tiling.FeatureWeighting) {
        this.$operator().setFeatureWeighting(value)
    }

    readonly $blendingBorder = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.blendingBorder
    })

    protected setBlendingBorder(value: number) {
        this.$operator().setBlendingBorder(value)
    }

    readonly $maxBlendingRadius = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.maxBlendingRadius
    })

    protected setMaxBlendingRadius(value: number) {
        this.$operator().setMaxBlendingRadius(value)
    }

    readonly $gradientCorrection = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.gradientCorrection
    })

    protected setGradientCorrection(value: boolean) {
        this.$operator().setGradientCorrection(value)
    }

    readonly $filterMode = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.filterMode
    })

    protected setFilterMode(value: Tiling.FilterMode) {
        this.$operator().setFilterMode(value)
    }

    readonly $filterRadius = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.filterRadius
    })

    protected setFilterRadius(value: number) {
        this.$operator().setFilterRadius(value)
    }

    readonly $patternType = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.patternType
    })

    protected setPatternType(value: Tiling.PatternType) {
        this.$operator().setPatternType(value)
    }

    readonly $edgeAlignmentWindowSize = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.edgeAlignmentWindowSize
    })

    protected setEdgeAlignmentWindowSize(value: number) {
        this.$operator().setEdgeAlignmentWindowSize(value)
    }

    readonly $edgeAlignmentSmoothingIterations = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.edgeAlignmentSmoothingIterations
    })

    protected setEdgeAlignmentSmoothingIterations(value: number) {
        this.$operator().setEdgeAlignmentSmoothingIterations(value)
    }

    readonly $alignIndividualMaps = computed(() => {
        this.$operator().$paramsVersion()
        return this.$operator().tilingParams.alignIndividualMaps
    })

    protected setAlignIndividualMaps(value: boolean) {
        this.$operator().setAlignIndividualMaps(value)
    }

    readonly $tilingCutoutActive = computed(() => this.$operator().$isTilingCutoutActive())

    cutoutClick() {
        this.$operator().toggleCutoutActive()
    }
}
